import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { FileDataDisplay } from "#src/components/DataDisplay/FileDataDisplay";
import { GeocoordinateDataDisplay } from "#src/components/DataDisplay/GeocoordinateDataDisplay";
import { linkToAssetDetailPage } from "#src/utils/links";
import { InfiniteData } from "@tanstack/react-query";
import {
  ArrayDataDisplay,
  BooleanDataDisplay,
  CalculatedFieldDataDisplay,
  DataTable,
  DateRangeDataDisplay,
  MathDataDisplay,
  NumberDataDisplay,
  TextDataDisplay,
  type HeaderType,
} from "@validereinc/common-components";
import {
  AssetTypeType,
  AttributeDataType,
  FileUploadedSchema,
  FormSchemaMeasurementQuestionType,
  FormSchemaType,
  FormSubmissionAdapter,
  FormSubmissionLookupDetailsQuestionAnswerType,
  FormSubmissionMeasurementQuestionAnswerType,
  FormSubmissionWithAnswersType,
  getFilterObject,
  GetListResponseType,
  isMeasurementFormQuestion,
  LookupWithDetailsValueSchema,
  type FormSubmissionAnswersBySectionMapType,
  type FormSubmissionAnswerType,
  type FormSubmissionType,
  type MeasurementUnitType,
} from "@validereinc/domain";
import { Forms } from "@validereinc/domain-controllers";
import { DateFormats, DateTimeValueSchemas } from "@validereinc/utilities";
import parseISO from "date-fns/parseISO";
import { isNull } from "es-toolkit/compat";
import { produce } from "immer";
import React from "react";

const { CalculatedFieldEquationController } = Forms.view;
const {
  findAnswerForQuestion,
  findSectionUsingName,
  FormCalculatedFieldService,
  resolveQuestionConfiguration,
} = Forms.logic;

export const renderFormSubmissionAnswer = (
  answer?: FormSubmissionAnswerType | null,
  questionId?: string,
  params?: {
    measurementUnits: MeasurementUnitType[];
    getPrecisionByType: (type: string) => number | null;
    schema: FormSchemaType;
    sectionId: string;
    sectionIdx: number;
    allAnswers: FormSubmissionAnswersBySectionMapType;
    allQuestions: FormSchemaType["config"]["questions"];
  }
) => {
  if (!questionId || !params) return "-";
  const {
    measurementUnits,
    getPrecisionByType,
    schema,
    sectionId,
    sectionIdx,
    allAnswers,
    allQuestions,
  } = params;

  const question = schema.config.questions[questionId];

  if (!answer) return "-";

  if (question.equation) {
    // Resolve the question configuration to get the correct equation, measurement_unit, and measurement_type
    // based on any active conditional overrides
    const resolvedQuestion = resolveQuestionConfiguration(
      questionId,
      sectionId,
      sectionIdx,
      schema,
      allAnswers
    );

    // If the resolved question doesn't have an equation (edge case where override removes it), bail out
    if (!resolvedQuestion.equation) return "-";

    const equationVariablesAndValues =
      FormCalculatedFieldService.getEquationVariablesAndValuesFromSubmission(
        resolvedQuestion.equation,
        sectionId,
        sectionIdx,
        allAnswers,
        allQuestions
      );

    // If the answer is derived from another equation with a date or time unit, the BE calculates and stores it in minutes.
    // Therefore, we must convert the unit.
    const convertedAnswer = FormCalculatedFieldService.convertAnswerIfNecessary(
      equationVariablesAndValues,
      answer.value as number,
      resolvedQuestion,
      schema.config.questions
    );

    const displayMeasurementUnitId =
      FormCalculatedFieldService.getMeasurementUnitFromQuestion(
        resolvedQuestion,
        false
      );
    const displayMeasurementUnit = measurementUnits.find(
      ({ id }) => displayMeasurementUnitId === id
    );

    return (
      <CalculatedFieldDataDisplay
        value={convertedAnswer ?? (answer.value as number)}
        displaySlot={(props) => (
          <NumberDataDisplay
            {...props}
            unit={
              displayMeasurementUnit?.name?.symbol ?? displayMeasurementUnitId
            }
          />
        )}
        alignment="right"
        showHint
        aria-invalid={false}
        hintSlot={
          <>
            This was calculated using the equation:{" "}
            <CalculatedFieldEquationController
              equation={resolvedQuestion.equation}
              questionsMap={schema.config.questions}
              sourceFieldValuesMap={equationVariablesAndValues}
              getTokens
            >
              {({ tokens }) => (
                <MathDataDisplay.Root>
                  <MathDataDisplay
                    colorScheme="light"
                    style={{ whiteSpace: "nowrap", fontSize: 14 }}
                  >
                    {tokens.map((t, idx) => {
                      if (t.startGroup || t.endGroup) {
                        return <span key={idx}>{t.displayValue}</span>;
                      } else if (t.isVariable) {
                        const displayMeasurementUnit = measurementUnits.find(
                          ({ id }) => t.unit === id
                        );
                        const unit =
                          displayMeasurementUnit?.name?.symbol ?? t.unit;

                        return (
                          <MathDataDisplay.InteractiveGroup
                            key={idx}
                            value={t.value}
                            displayValue={
                              t.value !== undefined &&
                              t.value !== null &&
                              String(t.value) !== ""
                                ? t.value.toString()
                                : null
                            }
                            description={`This is the answer from the question of the same name.${unit ? ` In units: "${unit}"` : ""}`}
                          >
                            &quot;{t.displayValue}&quot;
                          </MathDataDisplay.InteractiveGroup>
                        );
                      } else {
                        return (
                          <span
                            key={idx}
                            dangerouslySetInnerHTML={{
                              __html: t.displayValue,
                            }}
                          ></span>
                        );
                      }
                    })}
                  </MathDataDisplay>
                </MathDataDisplay.Root>
              )}
            </CalculatedFieldEquationController>
          </>
        }
      />
    );
  }

  if (isMeasurementFormQuestion(question)) {
    // Resolve the question configuration to get the correct measurement_unit and measurement_type
    // based on any active conditional overrides
    const resolvedQuestion = resolveQuestionConfiguration(
      questionId,
      sectionId,
      sectionIdx,
      schema,
      allAnswers
    ) as FormSchemaMeasurementQuestionType;

    const displayMeasurementUnit = measurementUnits.find(
      ({ id }) => resolvedQuestion.measurement_unit === id
    );
    const precision = getPrecisionByType(resolvedQuestion.measurement_type);

    const displayedNumber = (
      <NumberDataDisplay
        value={answer.value as number}
        unit={
          displayMeasurementUnit?.name?.symbol ??
          resolvedQuestion.measurement_unit
        }
        formattingOpts={{
          ...(precision !== null
            ? {
                overrideOpts: {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: precision,
                },
              }
            : {}),
        }}
      />
    );

    if (
      !(answer as FormSubmissionMeasurementQuestionAnswerType)?.subject_type ||
      !(answer as FormSubmissionMeasurementQuestionAnswerType)?.subject_id
    )
      return displayedNumber;

    return (
      <RoutingLink
        style={{ display: "block", textAlign: "right" }}
        to={{
          pathname: linkToAssetDetailPage(
            (answer as FormSubmissionMeasurementQuestionAnswerType)
              .subject_type as AssetTypeType,
            (answer as FormSubmissionMeasurementQuestionAnswerType)
              .subject_id ?? ""
          ),
          search: "?tab=measurements",
        }}
      >
        {displayedNumber}
      </RoutingLink>
    );
  }

  switch (question.data_type) {
    case "geo_point":
      return (
        <GeocoordinateDataDisplay
          coordinates={answer.value as [number, number]}
        />
      );
    case "file": {
      const verifiedAnswer = FileUploadedSchema.safeParse(answer.value);

      if (verifiedAnswer.error) {
        return "-";
      }

      return (
        <FileDataDisplay
          fileName={verifiedAnswer.data.name}
          fileId={verifiedAnswer.data.ref}
          prefetch={false}
        />
      );
    }
    case "lookup": {
      const verifiedAnswer = LookupWithDetailsValueSchema.safeParse(answer);

      if (verifiedAnswer.error) {
        return (
          (answer as FormSubmissionLookupDetailsQuestionAnswerType)?.name ?? "-"
        );
      }

      if (verifiedAnswer.data.entity_type === "form_submission") {
        return verifiedAnswer.data.name;
      }

      return (
        <RoutingLink
          to={linkToAssetDetailPage(
            verifiedAnswer.data.entity_type as AssetTypeType,
            verifiedAnswer.data.value
          )}
        >
          {verifiedAnswer.data.name}
        </RoutingLink>
      );
    }
    case "multi-pick-list": {
      return <ArrayDataDisplay value={answer.value as string[]} />;
    }
    case "pick-list": {
      return <TextDataDisplay value={answer.value as string} />;
    }
    case "date": {
      return (
        <DataTable.DataRow.DateCell
          value={answer.value as string}
          withTime={false}
        />
      );
    }
    case "date-time": {
      return (
        <DataTable.DataRow.DateCell
          value={answer.value as string}
          withTime
        />
      );
    }
    case "date-time-range": {
      const verifiedAnswer = DateTimeValueSchemas.dateTimeRangeSimple.safeParse(
        answer.value
      );

      if (verifiedAnswer.error) {
        return "-";
      }

      return (
        <DateRangeDataDisplay
          displayFormat={DateFormats.DATE_TIME}
          value={{
            from: parseISO(verifiedAnswer.data[0]),
            to: parseISO(verifiedAnswer.data[1]),
          }}
        />
      );
    }
    case "boolean": {
      return <BooleanDataDisplay value={answer.value as boolean} />;
    }
    case "number": {
      return (
        <DataTable.DataRow.NumberCell
          value={answer.value as number}
          formattingOpts={{
            ...(Number(answer.value) > 10 ** 6
              ? { fractionDigits: 6 }
              : { overrideOpts: { maximumFractionDigits: 3 } }),
          }}
        />
      );
    }
    case "integer": {
      return (
        <DataTable.DataRow.NumberCell
          value={answer.value as number}
          formattingOpts={
            Number(answer.value) > 10 ** 6
              ? { fractionDigits: 6 }
              : { overrideOpts: { maximumFractionDigits: 3 } }
          }
        />
      );
    }
    case "string": {
      if (typeof answer.value !== "string") return "-";

      return <DataTable.DataRow.TextCell value={answer.value} />;
    }
    default:
      return "n/a";
  }
};

/**
 * Translate an existing form answers filter object to another filter object
 * that conforms to the filtering restrictions based on the associated form
 * schema
 * @param filters the form answers filter object
 * @param formSchema the associated form schema
 * @returns a filter object that works with the form answers filtering endpoint
 */
export const getFormattedFiltersBasedOnFormSchema = (
  filters: Omit<
    Parameters<typeof FormSubmissionAdapter.answers.getListV2>[0]["filters"],
    "created_at" | "created_by" | "status"
  >,
  formSchema: FormSchemaType
) => {
  if (!filters || !formSchema) return {};

  return Object.entries(filters).reduce<
    Record<string, string | number | boolean | string[] | null>
  >((newFilters, [filterKey, filterValue]) => {
    const [sectionId, questionId] = filterKey.split(":");
    const matchingSectionConfig = formSchema.config?.sections?.find(
      (s) => s.id === sectionId
    );
    const matchingQuestionConfig = formSchema.config.questions[questionId];

    if (
      (!matchingSectionConfig && !matchingQuestionConfig) ||
      isNull(filterValue) ||
      (typeof filterValue === "string" && filterValue === "")
    ) {
      return newFilters;
    }

    switch (matchingQuestionConfig.type) {
      case "question": {
        switch (matchingQuestionConfig.data_type) {
          // IMPROVE: some data types are not supported for filtering at this time
          case AttributeDataType.DATE_TIME_RANGE:
          case AttributeDataType.FILE:
            return newFilters;
          case AttributeDataType.DATE:
            // REVIEW: Strip the time from the timestamp and only return the date as the BE doesn't support filtering on such a date string
            newFilters[filterKey] =
              typeof filterValue === "string"
                ? filterValue.split("T")[0]
                : (filterValue as string);
            break;
          case AttributeDataType.NUMBER:
          case AttributeDataType.INTEGER:
            newFilters[filterKey] = Number(filterValue);
            break;
          // REVIEW: geo point inputs return an array of [null, null] when no value is provided which is smelly
          case AttributeDataType.GEO_POINT: {
            if (
              !filterValue ||
              !Array.isArray(filterValue) ||
              (Array.isArray(filterValue) &&
                (!filterValue[0] || !filterValue[1]))
            ) {
              return newFilters;
            }

            // REVIEW: BE requires a very strange syntax to query arrays
            newFilters[filterKey] = JSON.stringify(
              filterValue.map((v) => Number(v))
            ).replace(/,/g, ", ");
            break;
          }
          case AttributeDataType.MULTI_PICK_LIST: {
            if (!Array.isArray(filterValue)) return newFilters;

            // REVIEW: BE requires a very strange syntax to query arrays
            newFilters[filterKey] = JSON.stringify(filterValue).replace(
              /,/g,
              ", "
            );
            break;
          }
          default:
            newFilters[filterKey] = filterValue as string;
            break;
        }
        break;
      }
      case "measurement": {
        newFilters[filterKey] = Number(filterValue);
        break;
      }
    }

    return newFilters;
  }, {});
};

export const renderFormSectionName = (
  sectionId: string,
  section_index: number,
  schema: FormSchemaType
) => {
  const sectionName =
    schema?.config?.sections?.find((section) => section.id === sectionId)
      ?.name || "";

  const sectionIndexAsNumber = Number(section_index);
  return `${sectionName} ${!isNaN(sectionIndexAsNumber) ? sectionIndexAsNumber + 1 : ""}`;
};

export const getHeadersFromSchema = (
  schema: FormSchemaType,
  {
    measurementUnits,
    getPrecisionByType,
  }: {
    measurementUnits: MeasurementUnitType[];
    getPrecisionByType: (type: string) => number | null;
  }
): Array<HeaderType<FormSubmissionType>> => {
  return schema.config?.sections?.map<HeaderType<FormSubmissionType>>((s) => {
    return {
      key: s.id,
      label: `${s.name}${s.is_repeatable ? " (Repeatable)" : ""}`,
      headers: s.questions.map((qid) => ({
        label: schema.config.questions[qid].prompt,
        key: `${s.id}.${qid}`,
        tooltip: schema.config.questions[qid].description,
        minWidth: 180,
        renderComponent: ({ item }) => {
          const section = (() => {
            if (Array.isArray(item.answers[s.id])) {
              return s;
            }
            // If we cannot find answers for a section ID, the section ID might have changed.
            // Fallback to using the section's name to find a section in the schema that matches the one in the submission.
            return findSectionUsingName(schema, item, s.id);
          })();

          if (!item.answers || !section) {
            return <>n/a</>;
          }

          const firstFilledSectionIdx = item.answers[section.id].findIndex(
            (sec) => !!sec
          );

          const answer = findAnswerForQuestion(
            item.answers,
            qid,
            section.id,
            firstFilledSectionIdx,
            { findFallbackByPrompt: true },
            schema,
            item.form_schema
          );

          const thisAndOtherRepeatedSectionsWithAnswers = item.answers[
            section.id
          ].reduce((count, sec, idx) => {
            if (
              idx === firstFilledSectionIdx ||
              !sec ||
              !Object.keys(sec).length ||
              sec?.[qid] === undefined
            ) {
              return count;
            }

            return count + 1;
          }, 0);

          // IMPROVE: better UI to show multiple answers
          return thisAndOtherRepeatedSectionsWithAnswers > 1 ||
            (thisAndOtherRepeatedSectionsWithAnswers >= 1 &&
              item.answers[section.id].length > 1) ? (
            <>
              {answer !== undefined
                ? "(multiple answers)"
                : "(answers in other sections)"}
            </>
          ) : (
            renderFormSubmissionAnswer(answer, qid, {
              schema,
              measurementUnits,
              getPrecisionByType,
              sectionId: section.id,
              sectionIdx: 0,
              allAnswers: item.answers,
              allQuestions: schema.config.questions,
            })
          );
        },
      })),
    };
  });
};

/**
 * When searching form submissions by their answers, matching IDs are stored in this variable.
 * Then in another query, {id: {$in: filteredSubmissionIdsByAnswerSearch}} is included in the filters.
 *
 * @param answersQuery - query result from FormSubmissionAdapter.answers.getListV2
 * @returns form submissions ids filtered by answers
 */
export const getFilteredSubmissionIdsByAnswerSearch = (
  answersQuery:
    | InfiniteData<
        GetListResponseType<FormSubmissionWithAnswersType> | undefined
      >
    | undefined
) => {
  return (
    answersQuery?.pages.flatMap((page) => page?.data.map((d) => d.id)) ?? []
  ).filter((validValue) => !!validValue) as string[];
};

/**
 * In domain package, empty array values are omitted.
 * Therefore `id: []` in queryPayload gets ignored and isn't even sent to BE.
 *
 * Suggested solution is to:
 * 1. Transform the filter to BE-compatible format (using getFilterObject)
 * 2. Then marking isAlreadyFormatted to true, so that FormSubmissionAdapter.getList doesn't re-format it
 * 3. Explicitly inject {id: {$in: filteredAnswers}} to the filter value.
 *
 * @param queryPayloadWithoutAnswerFilter - query payload without answer filter
 * @param hasFormattedAnswerFilters - whether the answer filters have been formatted
 * @param filteredSubmissionIdsByAnswerSearch - form submissions ids filtered by answers
 * @returns query payload with answer filter
 */
export const getFormSubmissionQueryPayloadWithAnswerFilter = (
  queryPayloadWithoutAnswerFilter: Parameters<
    typeof FormSubmissionAdapter.getList
  >[0],
  hasFormattedAnswerFilters: boolean,
  filteredSubmissionIdsByAnswerSearch: string[]
) => {
  return produce(queryPayloadWithoutAnswerFilter, (draft) => {
    draft.filters = getFilterObject(draft.filters);
    draft.filters.isAlreadyFormatted = true;

    if (hasFormattedAnswerFilters) {
      draft.filters.id = {
        $in: filteredSubmissionIdsByAnswerSearch,
      };
    }
  });
};
